import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { IconCash, IconPlus } from '@tabler/icons';
import { useParams } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const STATUS_LABEL = {
    draft: { label: 'Rascunho', color: 'warning' },
    confirmed: { label: 'Confirmada', color: 'success' },
    cancelled: { label: 'Cancelada', color: 'default' }
};

const PayrollRunDetail = () => {
    const { id } = useParams();
    const [appId, setAppId] = useState(null);
    const [payrollRun, setPayrollRun] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [confirming, setConfirming] = useState(false);
    const [lineDialogItem, setLineDialogItem] = useState(null);
    const [lineForm, setLineForm] = useState({ kind: 'earning', description: '', amount: '' });
    const [savingLine, setSavingLine] = useState(false);
    const mountedRef = useRef(true);

    useEffect(
        () => () => {
            mountedRef.current = false;
        },
        []
    );

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                if (mountedRef.current) setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadPayrollRun();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const loadPayrollRun = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/payroll_runs/${id}/`);
            if (mountedRef.current) setPayrollRun(res.data);
        } catch (error) {
            console.error(error);
            if (mountedRef.current) setStatus({ type: 'error', message: 'Erro ao carregar folha' });
        }
    };

    const handleOpenLineDialog = (item) => {
        setLineForm({ kind: 'earning', description: '', amount: '' });
        setLineDialogItem(item);
    };

    const handleSaveLine = async () => {
        if (!lineForm.description || !lineForm.amount || Number(lineForm.amount) <= 0) {
            setStatus({ type: 'error', message: 'Informe a descrição e um valor maior que zero' });
            return;
        }
        setSavingLine(true);
        try {
            await api.post(`v1/apps/${appId}/payroll_items/${lineDialogItem.id}/lines/`, lineForm);
            setLineDialogItem(null);
            loadPayrollRun();
        } catch (error) {
            console.error(error);
            if (mountedRef.current) setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao adicionar lançamento' });
        } finally {
            if (mountedRef.current) setSavingLine(false);
        }
    };

    const handleConfirm = async () => {
        if (!window.confirm('Confirmar esta folha? Isso vai gerar uma Conta a Pagar por colaborador.')) return;
        setConfirming(true);
        try {
            await api.post(`v1/apps/${appId}/payroll_runs/${id}/confirm/`);
            loadPayrollRun();
        } catch (error) {
            console.error(error);
            if (mountedRef.current) setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao confirmar folha' });
        } finally {
            if (mountedRef.current) setConfirming(false);
        }
    };

    if (!payrollRun) return null;

    const statusInfo = STATUS_LABEL[payrollRun.status] || { label: payrollRun.status, color: 'default' };

    return (
        <Box>
            <PageHeader
                icon={IconCash}
                gradient="blue"
                title={`Folha ${payrollRun.reference_month}`}
                subtitle={`Pagamento em ${payrollRun.pay_date}`}
                actions={
                    payrollRun.status === 'draft' && (
                        <Button variant="contained" onClick={handleConfirm} disabled={confirming}>
                            {confirming ? 'Confirmando...' : 'Confirmar Folha'}
                        </Button>
                    )
                }
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
                <Chip label={statusInfo.label} color={statusInfo.color} />
                <Typography variant="subtitle1">Total líquido: R$ {payrollRun.total_net}</Typography>
            </Stack>

            {payrollRun.items.map((item) => (
                <GlassCard key={item.id} sx={{ mb: 2 }}>
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="subtitle1">{item.colaborador_name}</Typography>
                            {payrollRun.status === 'draft' && (
                                <Button size="small" startIcon={<IconPlus size={16} />} onClick={() => handleOpenLineDialog(item)}>
                                    Lançamento
                                </Button>
                            )}
                        </Stack>
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell>Salário base</TableCell>
                                    <TableCell align="right">R$ {item.base_salary}</TableCell>
                                </TableRow>
                                {item.lines.map((line) => (
                                    <TableRow key={line.id}>
                                        <TableCell>
                                            {line.description} ({line.kind === 'earning' ? 'Provento' : 'Desconto'})
                                        </TableCell>
                                        <TableCell align="right">
                                            {line.kind === 'deduction' ? '-' : ''}R$ {line.amount}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell>
                                        <strong>Líquido</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>R$ {item.net_amount}</strong>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </CardContent>
                </GlassCard>
            ))}

            <Dialog open={Boolean(lineDialogItem)} onClose={() => setLineDialogItem(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Novo Lançamento</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select
                            label="Tipo"
                            fullWidth
                            value={lineForm.kind}
                            onChange={(e) => setLineForm({ ...lineForm, kind: e.target.value })}
                        >
                            <MenuItem value="earning">Provento</MenuItem>
                            <MenuItem value="deduction">Desconto</MenuItem>
                        </TextField>
                        <TextField
                            label="Descrição"
                            fullWidth
                            required
                            value={lineForm.description}
                            onChange={(e) => setLineForm({ ...lineForm, description: e.target.value })}
                        />
                        <TextField
                            label="Valor"
                            type="number"
                            fullWidth
                            required
                            value={lineForm.amount}
                            onChange={(e) => setLineForm({ ...lineForm, amount: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLineDialogItem(null)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSaveLine} disabled={savingLine}>
                        {savingLine ? 'Salvando...' : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PayrollRunDetail;
